import {
  buildExternalConfig,
  buildWorkforceConfig,
  type AuthEnv,
  type IdentityPlane,
  type MsalConfiguration,
  withFreshSignIn,
} from '@brush/auth';

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';

const env: AuthEnv = {
  clientId,
  tenant: import.meta.env.VITE_ENTRA_TENANT ?? '',
  redirectUri: window.location.origin,
  apiScope: import.meta.env.VITE_API_SCOPE ?? '',
};

/**
 * Which tenant players sign in against.
 *
 * Most players are expected to arrive through Entra External ID with social or email sign-up,
 * but players can also exist in the workforce tenant — staff who play, or an interim setup
 * before the External ID tenant exists. The two use different authority shapes, so the plane
 * is configuration rather than a code change.
 *
 * Either way a player is simply an authenticated caller holding no app roles: every mutation
 * requires `admin` or `room`, which no player is assigned. What they may *read* is the tiered
 * model of ADR 0010 Decision 5 — public, member, self — and not, as this comment used to say,
 * everything. That sentence was true of the plane and stopped being true of the product.
 *
 * The two planes also differ in what `tenant` and `apiScope` mean, which is configuration rather
 * than code and is written down where the values are set (docs/entra-setup.md §3.6): the scope
 * names an API registration in the external tenant rather than the workforce one, and while both
 * planes take a tenant GUID they reach different directories with it. (An earlier version of this
 * comment said the external tenant is named by its subdomain. It is not — `buildExternalConfig`
 * carries the issuer shape that decides it.)
 */
export const identityPlane: IdentityPlane =
  import.meta.env.VITE_IDENTITY_PLANE === 'workforce' ? 'workforce' : 'external';

/** Skip MSAL entirely for local dev against the dev-bypass API. */
export const authDisabled =
  import.meta.env.VITE_AUTH_DISABLED === 'true' ||
  !clientId ||
  clientId === '00000000-0000-0000-0000-000000000000';

/**
 * Never reaches MSAL: `main.tsx` mounts `DevApiProvider` instead of `AuthProvider` when
 * `authDisabled`. It exists so this module can export a `Configuration` unconditionally while
 * `buildExternalConfig`'s tenant guard runs only on the path that will really sign somebody in.
 *
 * That distinction is load-bearing rather than defensive. The zero-config dev default in
 * `.env.example` is `VITE_AUTH_DISABLED=true` with a placeholder tenant on the external plane, so
 * a guard that fired here would throw at module scope and take `pnpm dev` down on a fresh clone —
 * turning a check against one misconfiguration into a worse one affecting everybody.
 */
const DISABLED: MsalConfiguration = { auth: { clientId } };

/**
 * `withFreshSignIn` because this app is the one that opens a popup — erasing an account asks for a
 * sign-in made just now — and MSAL takes the popup's timeout from the configuration alone. The reason
 * for the value is on `FRESH_SIGN_IN_TIMEOUT_MS`.
 */
export const msalConfig: MsalConfiguration = authDisabled
  ? DISABLED
  : withFreshSignIn(identityPlane === 'workforce' ? buildWorkforceConfig(env) : buildExternalConfig(env));

export const apiScope = env.apiScope;
export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
